import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { axiosInstance } from '../../../helpers/api_helper';
import {
  PackageRankReward,
  PackageRankRewardStatus,
  PackageRankRewardsState
} from './types';
import { toast } from 'react-toastify';
import { handleValidDate, handleValidTime } from '../../../helpers/helpers';

export const getPackageRankRewards = createAsyncThunk(
  'packageRankRewards/fetch',
  async (params: URLSearchParams) => {
    const response = await axiosInstance.get('/package/package-rank/reward', {
      params
    });
    return response.data.data;
  }
);

export const markPackageRankRewardDelivered = createAsyncThunk(
  'packageRankRewards/delivered',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        '/package/package-rank/reward/status/delivered',
        {
          userPackageRankRewardId: id
        }
      );
      toast.success(response.data.message);

      return id;
    } catch (error: any) {
      toast.error(error.response.data.error);
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const exportRankReward = createAsyncThunk(
  'packageRankRewards/exportRankReward',
  async (params: URLSearchParams) => {
    const response = await axiosInstance.get(
      '/package/package-rank/reward/export',
      {
        params
      }
    );

    try {
      if (response?.data?.data?.usersPackageRankRewards) {
        const dataForExport = response?.data?.data?.usersPackageRankRewards.map(
          (u: any) => {
            return {
              userId: u?.userId,
              packageRankId: u?.packageRank?.packageRankId,
              package: u?.packageRank?.name,
              amount: u?.amount,
              reward: u?.packageRankReward?.name,
              dateCreated: u?.dateCreated
                ? `${handleValidDate(u.dateCreated)}, ${handleValidTime(
                    u.dateCreated
                  )}`
                : '',
              status:
                u?.status === PackageRankRewardStatus.PENDING
                  ? 'Pending'
                  : 'Delivered'
            };
          }
        );

        return dataForExport;
      }

      if (response.data.message) {
        toast.success(response.data.message);
      }
    } catch (error: any) {
      toast.error(error.response.data.error);
    }
  }
);

export const exportHeaders = [
  { label: 'User', key: 'userId' },
  { label: 'Package Rank ID', key: 'packageRankId' },
  { label: 'Package', key: 'package' },
  { label: 'Amount', key: 'amount' },
  { label: 'Reward', key: 'reward' },
  { label: 'Status', key: 'status' },
  { label: 'Date Created', key: 'dateCreated' }
];

const initialState = {
  data: [],
  dataForExport: [],
  totalCount: 0,
  loading: false,
  loadingExport: false
} as PackageRankRewardsState;

const packageRankRewardsSlice = createSlice({
  name: 'packageRankRewards',
  initialState,
  reducers: {
    ordered: (state) => {
      state.loading = true;
    },
    removeRankRewardForExport(state) {
      state.dataForExport = [];
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getPackageRankRewards.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getPackageRankRewards.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.data = payload.usersPackageRankRewards as PackageRankReward[];
      state.totalCount = payload.count;
    });
    builder.addCase(getPackageRankRewards.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(markPackageRankRewardDelivered.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(
      markPackageRankRewardDelivered.fulfilled,
      (state, { payload }) => {
        state.loading = false;
        const index = state.data.findIndex((p) => p.id === payload);
        state.data[index].status = PackageRankRewardStatus.DELIVERED;
      }
    );
    builder.addCase(markPackageRankRewardDelivered.rejected, (state) => {
      state.loading = false;
    });

    //loading export
    builder.addCase(exportRankReward.pending, (state) => {
      state.loadingExport = true;
    });
    builder.addCase(exportRankReward.fulfilled, (state, { payload }) => {
      state.loadingExport = false;
      state.dataForExport = payload;
    });
    builder.addCase(exportRankReward.rejected, (state) => {
      state.loadingExport = false;
    });
  }
});

export const { ordered, removeRankRewardForExport } =
  packageRankRewardsSlice.actions;
export default packageRankRewardsSlice.reducer;
